import {
  EmailAuthProvider,
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '../firebase';

export const GOOGLE_PROVIDER_ID = 'google.com';
export const PASSWORD_PROVIDER_ID = 'password';

/**
 * @param {string} email
 * @returns {Promise<string[]>}
 */
export async function getSignInMethodsForEmail(email) {
  const normalized = (email || '').trim();
  if (!normalized) return [];
  try {
    return await fetchSignInMethodsForEmail(auth, normalized);
  } catch (err) {
    console.warn('fetchSignInMethodsForEmail:', err);
    return [];
  }
}

/**
 * True when the email is registered with Google but has no password provider yet.
 * @param {string[]} methods
 */
export function isGoogleOnlySignIn(methods) {
  return (
    methods.includes(GOOGLE_PROVIDER_ID) &&
    !methods.includes(PASSWORD_PROVIDER_ID)
  );
}

/**
 * Link email/password to the currently signed-in user (e.g. after Google sign-in).
 * @param {import('firebase/auth').User} user
 * @param {string} email
 * @param {string} password
 */
export async function linkEmailPasswordToUser(user, email, password) {
  const credential = EmailAuthProvider.credential(email.trim(), password);
  try {
    return await linkWithCredential(user, credential);
  } catch (err) {
    if (err.code === 'auth/provider-already-linked') {
      return null;
    }
    throw err;
  }
}

/**
 * Sign in with email/password, then link a pending Google OAuth credential.
 * @param {string} email
 * @param {string} password
 * @param {import('firebase/auth').AuthCredential} googleCredential
 */
export async function signInWithPasswordAndLinkGoogle(email, password, googleCredential) {
  const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
  try {
    await linkWithCredential(userCredential.user, googleCredential);
  } catch (err) {
    if (err.code !== 'auth/provider-already-linked') {
      throw err;
    }
  }
  return userCredential;
}

/**
 * @param {import('firebase/auth').AuthError} error
 * @returns {import('firebase/auth').AuthCredential | null}
 */
export function googleCredentialFromAuthError(error) {
  return GoogleAuthProvider.credentialFromError(error);
}

/**
 * @param {import('firebase/auth').UserCredential} userCredential
 * @returns {import('firebase/auth').AuthCredential | null}
 */
export function googleCredentialFromResult(userCredential) {
  return GoogleAuthProvider.credentialFromResult(userCredential);
}

/**
 * @param {import('firebase/auth').User} user
 * @param {import('firebase/auth').AuthCredential} googleCredential
 */
export async function linkGoogleCredentialToUser(user, googleCredential) {
  if (!googleCredential) {
    throw new Error('Missing Google credential.');
  }
  try {
    return await linkWithCredential(user, googleCredential);
  } catch (err) {
    if (err.code === 'auth/provider-already-linked') {
      return null;
    }
    throw err;
  }
}

/**
 * @param {string | null | undefined} signedInEmail
 * @param {string} expectedEmail
 */
export function emailsMatchForLinking(signedInEmail, expectedEmail) {
  return (signedInEmail || '').trim().toLowerCase() === (expectedEmail || '').trim().toLowerCase();
}
